import { AddressResource, CityResource, VendorResource } from '@sigmacloud/sigma-client/dist/resources'
import Vue from 'vue'

/*
* AG Grid Methods here
*/
export default Vue.extend({
  methods: {
    intlNumberFormatter (number, locale, options) {
      if (typeof number === 'string') number = parseFloat(number)
      if (typeof number !== 'number' && !isNaN(Number(number))) return null
      const hideDecimals = (this.displayOptions || {}).hideDecimals || false
      const useParentheses = (this.displayOptions || {}).useParenthesesForNegative || false

      if (!locale) locale = 'en-us'

      if (!options) {
        options = {
          style: 'decimal',
          useGrouping: true,
          minimumFractionDigits: hideDecimals ? 0 : 2,
          maximumFractionDigits: hideDecimals ? 0 : 2
        }
      }

      let result = new Intl.NumberFormat(locale, options).format(number)

      // -0 should be just 0
      if (result === '-0' || result === '-0.00') {
        result = new Intl.NumberFormat(locale, options).format(0)
      }

      // currencySign option is not working on many browsers so manually handle
      if (useParentheses && result.slice(0, 1) === '-') {
        result = '(' + result.slice(1) + ')'
      }

      return result
    },

    reporterMath (oldValue, newValue) {
      if (!newValue) return oldValue

      // eslint-disable-next-line
      if (typeof newValue === 'string' && newValue.match(/^[-*=+\/]/)) {
        const operator = newValue.slice(0, 1)
        const slicedValue = parseFloat(newValue.slice(1))
        if (operator === '+') return (oldValue += slicedValue)
        else if (operator === '-') return (oldValue -= slicedValue)
        else if (operator === '*') return (oldValue *= slicedValue)
        else if (operator === '/') return (oldValue /= slicedValue)
        else if (operator === '=') return slicedValue
      } else if (typeof newValue === 'number') {
        return newValue
      } else if (!isNaN(Number(newValue))) {
        return parseFloat(newValue)
      } else {
        return oldValue
      }
    },

    customSum (params) {
      let sum = 0
      params.values.forEach((value) => {
        const number = parseFloat(String(value || 0).replace(/,/g, ''))
        if (number && !isNaN(number)) sum += number
      })
      if (isNaN(sum) || sum === 0) {
        return ''
      } else {
        return sum.toFixed(2)
      }
    },

    customList (values) {
      const list = []
      values.forEach((value) => {
        if (value && list.indexOf(value) === -1) list.push(value)
      })
      if (list.length > 0) {
        return list.join(',')
      } else {
        return ''
      }
    },

    customSumSelected (params) {
      let sum = 0
      if (params.values && params.rowNode.allChildrenCount) {
        params.rowNode.allLeafChildren.filter((node) => {
          if (node.selected && node.data.attributes) {
            const number = parseFloat(String(node.data.attributes.amount_decimal || 0).replace(/,/g, ''))
            if (number && !isNaN(number)) sum += number
          }
        })
      }
      return sum.toFixed(2)
    },

    buildTransLink (data) {
      // Need this to make sure we get a valid link while on standalone builds
      const url = new URL(window.location.href)
      const standAloneUrls = ['cr.qa.app.sigmacloud.io', 'localhost']
      const appHost = standAloneUrls.includes(url.hostname) ? 'https://app.sigmacloud.io' : 'https://' + url.hostname

      let type
      switch (data.trans_type) {
        case 'AP':
          type = 'aps'
          break

        case 'PC':
          type = 'pettycash'
          break

        case 'PO':
          type = 'pos'
          break

        case 'PR':
          type = 'prs'
          break

        default:
          type = 'jes'
      }
      return '<a style="color:#007bff" target="_blank" href="' + appHost + '/' + type + '/' + data.subledger_id + '">View</a>'
    },

    // Returns an ID for a city
    async getCityId (newAddress = null, oldAddress = null) {
      let cityResource
      if (!newAddress && !oldAddress) return
      if (newAddress.city) {
        const query = { search: newAddress.city }

        // Since you can save a city with a state, look for that combo first
        if (newAddress.state) {
          query.state = newAddress.state
        } else if (oldAddress.state) {
          query.state = oldAddress.state
        }

        const lookupResources = await this.$resources.CityResource.list({ query })

        // Since these cities are throwaways, go ahead and take the first match
        if (lookupResources && lookupResources.count > 0) {
          cityResource = lookupResources.resources[0]
          return cityResource.id
        } else {
          cityResource = new CityResource()
          cityResource.set('code', newAddress.city.replace(/[^A-Za-z]/g, '').substr(0, 8) + (query.state || ''))
          cityResource.set('name', newAddress.city)
          if (query.state) cityResource.set('state', query.state)

          await cityResource.save()
          await cityResource.resolveRelated()
          return cityResource.id || null
        }
      } else if (oldAddress) {
        return oldAddress.city
      } else {
        return null
      }
    },

    // Create a new address with new/old data
    // The idea is that if someone is only chaning one field, we should use that new datum plus the old set
    // returns a CityResource
    async createNewAddress (newAddressData = null, oldAddress = null) {
      if (!newAddressData) return

      // Make sure theres enough to save the address.  If not, skip
      const line1 = newAddressData.line1 || oldAddress.line1 || ''
      const line2 = newAddressData.line2 || oldAddress.line2 || ''
      const postal = newAddressData.postal || oldAddress.postal || ''
      const country = newAddressData.country || oldAddress.country || null
      const state = newAddressData.state || oldAddress.state || null

      // City is now its own resource.  Look for existing cities, and if not found, create a new one.
      const city = await this.getCityId(newAddressData, oldAddress) || null

      if (line1 && city && postal && state) {
        const newResource = new AddressResource()
        newResource.attributes.line1 = line1
        newResource.attributes.line2 = line2
        newResource.attributes.city = city
        newResource.attributes.state = state
        newResource.attributes.postal = postal
        newResource.attributes.country = country
        await newResource.save()
        await newResource.resolveRelated()
        return newResource
      } else {
        return null
      }
    },

    async updateVendor (vendor, data) {
      const resultVendor = await VendorResource.detail(vendor)
      for (const key in data) {
        resultVendor.set(`${key}`, data[key])
      }
      await resultVendor.save()
    },
    buildLink (params) {
      if (['Checks', 'PaymentProcessing', 'CheckProcessing', 'Payments'].includes(this.$route.name)) {
        return params.value
      }
      const index = this.$route.path.lastIndexOf('/')
      const path = `${this.$route.path}${index + 1 !== this.$route.path.length ? '/' : ''}${params.data.id}`
      return `<span class="clickable-link" data-path="${path}" style="cursor: pointer;">${params.value}</span>`
    },
    getStyles (column) {
      if (column === 'amount') {
        return {
          'border-right-color': '#e2e2e2',
          'display': 'flex ',
          'align-items': 'center',
          'justify-content': 'end'
        }
      }
      if (column || ['Checks', 'PaymentProcessing', 'CheckProcessing', 'Payments'].includes(this.$route.name)) {
        return {
          'display': 'flex ',
          'align-items': 'center',
          'border-right-color': '#e2e2e2',
          'border-top': 'none',
          'border-bottom': 'none',
          'border-left': 'none'
        }
      }
      return {
        'display': 'flex ',
        'align-items': 'center',
        'color': 'blue',
        'border-right-color': '#e2e2e2',
        'border-top': 'none',
        'border-bottom': 'none',
        'border-left': 'none'
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/Mixins/SigmaClientNew/AgGridColumnMethods.js